import type { ViewNode } from './graphModel';
import type { FlowNodeData } from './useGraphLayout';

const sameValue = (a: unknown, b: unknown): boolean =>
  Object.is(a, b) ||
  (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((x, i) => Object.is(x, b[i])));

/**
 * The same node content: the same object, or the same fields, with arrays compared member by member. A group
 * node is a new object each time the view is selected, so identity alone would redraw every group card.
 */
export function sameViewNode(a: ViewNode, b: ViewNode): boolean {
  if (a === b) return true;
  const x = a as Record<string, unknown>;
  const y = b as Record<string, unknown>;
  const keys = Object.keys(x);
  return keys.length === Object.keys(y).length && keys.every((key) => sameValue(x[key], y[key]));
}

const sameData = (a: FlowNodeData, b: FlowNodeData) =>
  a.selected === b.selected &&
  a.focused === b.focused &&
  a.expanded === b.expanded &&
  a.expandable === b.expandable &&
  a.onCollapse === b.onCollapse &&
  a.onShowMore === b.onShowMore &&
  a.mentions === b.mentions &&
  sameViewNode(a.node, b.node);

export type NodeDataCache = {
  /**
   * Each node's data, keeping the object from the previous call for a node whose fields are all the same, so its
   * memoised card does not render again. Nodes not passed are dropped from the cache.
   */
  reuse(next: FlowNodeData[]): FlowNodeData[];
};

export function createNodeDataCache(): NodeDataCache {
  let byId = new Map<string, FlowNodeData>();
  return {
    reuse(next) {
      const kept = new Map<string, FlowNodeData>();
      const out = next.map((data) => {
        const prev = byId.get(data.node.id);
        const chosen = prev && sameData(prev, data) ? prev : data;
        kept.set(data.node.id, chosen);
        return chosen;
      });
      byId = kept;
      return out;
    },
  };
}
